import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ThumbsUp, ThumbsDown, RefreshCw, AlertTriangle, Filter, TrendingUp } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminFeedback() {
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [data, setData] = useState({ rows: [], aggregates: { top_bad_questions: [] } });
  const [ratingFilter, setRatingFilter] = useState("");
  const [regenerating, setRegenerating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/feedback", { params: ratingFilter ? { rating: ratingFilter } : {} });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) {
        setDenied(true);
      } else {
        toast.error("Failed to load feedback.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingFilter]);

  const regenerate = async (qid) => {
    if (!window.confirm("Delete this question so the next 'Generate more' click on that topic creates a fresh one?")) return;
    setRegenerating(qid);
    try {
      await api.post(`/admin/feedback/regenerate/${qid}`);
      toast.success("Question removed — a fresh one will be generated on demand.");
      await load();
    } catch (e) {
      toast.error("Regenerate failed.");
    } finally {
      setRegenerating(null);
    }
  };

  if (denied) {
    return (
      <div className="max-w-md mx-auto pm-card p-8 text-center mt-16" data-testid="admin-feedback-denied">
        <AlertTriangle size={40} className="mx-auto mb-3" style={{ color: "var(--weakness)" }} />
        <h1 className="font-display text-2xl font-bold mb-2">Admin only</h1>
        <p style={{ color: "var(--text-secondary)" }}>
          This inbox is locked to the PictureMed founder account. If that's you, log in with the admin email.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="admin-feedback-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="pm-badge mb-2" style={{ background: "var(--hl-blue)", color: "var(--primary)" }}>
            <ShieldCheck size={12} className="mr-1.5" /> Admin
          </div>
          <h1 className="font-display text-4xl font-bold">Feedback Inbox</h1>
          <p className="mt-2" style={{ color: "var(--text-secondary)" }}>
            Every question rating + one-line "why" your students submitted. Bad questions can be regenerated in-place.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} className="pm-btn-secondary inline-flex items-center gap-2" data-testid="admin-feedback-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <Link to="/app/admin/playground" className="pm-btn-primary inline-flex items-center gap-2" data-testid="admin-view-playground-btn">
            <TrendingUp size={14} /> Playground analytics
          </Link>
        </div>
      </div>

      {/* Top bad-questions callout */}
      {data.aggregates?.top_bad_questions?.length > 0 && (
        <div className="pm-card p-4 mb-6" style={{ borderColor: "var(--weakness)", borderWidth: 2 }} data-testid="admin-top-bad">
          <div className="flex items-center gap-2 mb-2 font-display font-bold">
            <AlertTriangle size={16} style={{ color: "var(--weakness)" }} />
            Questions with 3+ thumbs-down
          </div>
          <div className="space-y-1.5">
            {data.aggregates.top_bad_questions.map((b) => (
              <div key={b.question_id} className="flex items-center gap-2 text-sm">
                <span className="pm-badge shrink-0" style={{ background: "#fdecea", color: "var(--weakness)" }}>{b.count}× 👎</span>
                <span className="flex-1 truncate">{b.question_text} <span style={{ color: "var(--text-muted)" }}>· {b.topic_name}</span></span>
                <button
                  onClick={() => regenerate(b.question_id)}
                  disabled={regenerating === b.question_id}
                  className="pm-btn-primary text-xs py-1 px-3"
                  data-testid={`admin-regen-${b.question_id}`}
                >
                  {regenerating === b.question_id ? <Loader2 size={12} className="animate-spin" /> : "Regenerate"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 mb-4">
        <Filter size={14} style={{ color: "var(--text-muted)" }} />
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>Filter:</span>
        {[
          { v: "", label: "All" },
          { v: "up", label: "👍 Up" },
          { v: "down", label: "👎 Down" },
        ].map((f) => (
          <button
            key={f.v}
            onClick={() => setRatingFilter(f.v)}
            className="text-xs px-3 py-1 rounded-full transition-colors"
            style={{
              background: ratingFilter === f.v ? "var(--primary)" : "var(--muted)",
              color: ratingFilter === f.v ? "white" : "var(--text-secondary)",
            }}
            data-testid={`admin-filter-${f.v || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center" data-testid="admin-loading">
          <Loader2 className="animate-spin" size={18} /> Loading feedback…
        </div>
      ) : data.rows.length === 0 ? (
        <div className="pm-card p-8 text-center" data-testid="admin-empty">
          <div className="text-3xl mb-2">📮</div>
          <p style={{ color: "var(--text-secondary)" }}>No feedback yet with this filter.</p>
        </div>
      ) : (
        <div className="pm-card overflow-hidden" data-testid="admin-feedback-list">
          <table className="w-full text-sm" data-testid="admin-feedback-table">
            <thead style={{ background: "var(--muted)" }}>
              <tr>
                <th className="text-left p-3 font-display">Rating</th>
                <th className="text-left p-3 font-display">Topic / Question</th>
                <th className="text-left p-3 font-display">Reason</th>
                <th className="text-left p-3 font-display">User</th>
                <th className="text-left p-3 font-display">When</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.question_id}-${r.user_email}-${i}`} style={{ borderTop: "1px solid var(--border)" }} data-testid={`admin-row-${r.question_id}`}>
                  <td className="p-3">
                    {r.rating === "up" ? (
                      <span style={{ color: "var(--mastery)" }} className="inline-flex items-center gap-1"><ThumbsUp size={13} /> Up</span>
                    ) : (
                      <span style={{ color: "var(--weakness)" }} className="inline-flex items-center gap-1"><ThumbsDown size={13} /> Down</span>
                    )}
                  </td>
                  <td className="p-3 max-w-md">
                    <div className="font-semibold truncate">{r.topic_name}</div>
                    <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>"{r.question_text}"</div>
                  </td>
                  <td className="p-3 italic max-w-xs" style={{ color: "var(--text-secondary)" }}>
                    {r.reason || <span style={{ color: "var(--text-muted)" }}>—</span>}
                  </td>
                  <td className="p-3 text-xs">
                    <div className="font-semibold">{r.user_name || "(no name)"}</div>
                    <div style={{ color: "var(--text-muted)" }}>{r.user_email}</div>
                  </td>
                  <td className="p-3 text-xs" style={{ color: "var(--text-muted)" }}>{fmtDate(r.updated_at)}</td>
                  <td className="p-3">
                    {r.rating === "down" && (
                      <button
                        onClick={() => regenerate(r.question_id)}
                        disabled={regenerating === r.question_id}
                        className="text-xs underline"
                        style={{ color: "var(--primary)" }}
                        data-testid={`admin-regen-row-${r.question_id}`}
                      >
                        Regenerate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
